import { type AnchorHTMLAttributes, Fragment } from "react";

import type { ConversationMessage } from "../../../types";

interface TextMessageProps {
  message: ConversationMessage;
}

export default function TextMessage({ message }: TextMessageProps) {
  const { body } = message;

  return (
    <div className="flex flex-col w-full">
      <div className="hyphens-auto whitespace-pre-wrap text-sm break-words">
        {body.split(/(\s+)/).map((part, index) => {
          if (part.trim()) {
            //capture punctuation marks from start/end of string
            const punctuationRegex = /(^[.,;:/?\\]*)(.+?)([.,;:/?\\]*$)/g;
            const [_, prePunctuation, text, postPunctuation] =
              punctuationRegex.exec(part);

            // Regex to match URLs
            const urlRegex =
              /\b((?:https?:(?:\/{1,3}|[a-z0-9%])|[a-z0-9.-]+[.](?:com|games|net|org|edu|gov|mil|aero|asia|biz|cat|coop|info|int|jobs|mobi|museum|name|post|pro|tel|travel|xxx|ac|ad|ae|af|ag|ai|al|am|an|ao|aq|ar|as|at|au|aw|ax|az|ba|bb|bd|be|bf|bg|bh|bi|bj|bm|bn|bo|br|bs|bt|bv|bw|by|bz|ca|cc|cd|cf|cg|ch|ci|ck|cl|cm|cn|co|cr|cs|cu|cv|cx|cy|cz|dd|de|dj|dk|dm|do|dz|ec|ee|eg|eh|er|es|et|eu|fi|fj|fk|fm|fo|fr|ga|gb|gd|ge|gf|gg|gh|gi|gl|gm|gn|gp|gq|gr|gs|gt|gu|gw|gy|hk|hm|hn|hr|ht|hu|id|ie|il|im|in|io|iq|ir|is|it|je|jm|jo|jp|ke|kg|kh|ki|km|kn|kp|kr|kw|ky|kz|la|lb|lc|li|lk|lr|ls|lt|lu|lv|ly|ma|mc|md|me|mg|mh|mk|ml|mm|mn|mo|mp|mq|mr|ms|mt|mu|mv|mw|mx|my|mz|na|nc|ne|nf|ng|ni|nl|no|np|nr|nu|nz|om|pa|pe|pf|pg|ph|pk|pl|pm|pn|pr|ps|pt|pw|py|qa|re|ro|rs|ru|rw|sa|sb|sc|sd|se|sg|sh|si|sj|ja|sk|sl|sm|sn|so|sr|ss|st|su|sv|sx|sy|sz|tc|td|tf|tg|th|tj|tk|tl|tm|tn|to|tp|tr|tt|tv|tw|tz|ua|ug|uk|us|uy|uz|va|vc|ve|vg|vi|vn|vu|wf|ws|ye|yt|yu|za|zm|zw)\/)(?:[^\s()<>{}[\]]+|\([^\s()]*?\([^\s()]+\)[^\s()]*?\)|\([^\s]+?\))+(?:\([^\s()]*?\([^\s()]+\)[^\s()]*?\)|\([^\s]+?\)|[^\s`!()[\]{};:'".,<>?«»“”‘’])|(?:(?<!@)[a-z0-9]+(?:[.-][a-z0-9]+)*[.](?:com|net|org|games|edu|gov|mil|aero|asia|biz|cat|coop|info|int|jobs|mobi|museum|name|post|pro|tel|travel|xxx|ac|ad|ae|af|ag|ai|al|am|an|ao|aq|ar|as|at|au|aw|ax|az|ba|bb|bd|be|bf|bg|bh|bi|bj|bm|bn|bo|br|bs|bt|bv|bw|by|bz|ca|cc|cd|cf|cg|ch|ci|ck|cl|cm|cn|co|cr|cs|cu|cv|cx|cy|cz|dd|de|dj|dk|dm|do|dz|ec|ee|eg|eh|er|es|et|eu|fi|fj|fk|fm|fo|fr|ga|gb|gd|ge|gf|gg|gh|gi|gl|gm|gn|gp|gq|gr|gs|gt|gu|gw|gy|hk|hm|hn|hr|ht|hu|id|ie|il|im|in|io|iq|ir|is|it|je|jm|jo|jp|ke|kg|kh|ki|km|kn|kp|kr|kw|ky|kz|la|lb|lc|li|lk|lr|ls|lt|lu|lv|ly|ma|mc|md|me|mg|mh|mk|ml|mm|mn|mo|mp|mq|mr|ms|mt|mu|mv|mw|mx|my|mz|na|nc|ne|nf|ng|ni|nl|no|np|nr|nu|nz|om|pa|pe|pf|pg|ph|pk|pl|pm|pn|pr|ps|pt|pw|py|qa|re|ro|rs|ru|rw|sa|sb|sc|sd|se|sg|sh|si|sj|Ja|sk|sl|sm|sn|so|sr|ss|st|su|sv|sx|sy|sz|tc|td|tf|tg|th|tj|tk|tl|tm|tn|to|tp|tr|tt|tv|tw|tz|ua|ug|uk|us|uy|uz|va|vc|ve|vg|vi|vn|vu|wf|ws|ye|yt|yu|za|zm|zw)\b\/?(?!@)))/i;
            const urlMatchIndex = text.search(urlRegex);

            const emailRegex = /^[\w\-./]+@([\w-]+\.)+[\w-]{2,}$/;
            const hasEmailMatch = emailRegex.test(text);

            //this is either an email or a url
            if (urlMatchIndex !== -1 || hasEmailMatch) {
              const anchorProps: AnchorHTMLAttributes<HTMLAnchorElement> = {};
              //this is not an email, therefore a url
              if (urlMatchIndex === 0) {
                anchorProps.href = text.startsWith("http")
                  ? text
                  : `http://${text}`;
                anchorProps.target = "_blank";
                anchorProps.rel = "noreferrer";
              } else {
                anchorProps.href = `mailto:${text}`;
              }

              return (
                <Fragment key={index || part}>
                  {prePunctuation}
                  <a className="text-blue-600 underline" {...anchorProps}>
                    {text}
                  </a>
                  {postPunctuation}
                </Fragment>
              );
            }
          }

          return <Fragment key={index || part}>{part}</Fragment>;
        })}
      </div>
    </div>
  );
}
